import { ExternalLinkIcon } from "@chakra-ui/icons";
import {
  Box,
  Button,
  Flex,
  FormLabel,
  Grid,
  GridItem,
  Link,
  Text,
  Tooltip,
} from "@chakra-ui/react";
import { SYLA_EARLIEST_EVENT_DATE } from "@syla/shared/types/helpers/constants";
import { DataSourceCode } from "@syla/shared/types/models/DataSourceBase";
import { getWalletName } from "@syla/shared/types/models/WalletBase";
import { TransactionEvent } from "@syla/shared/types/responses/TransactionEvent";
import React, { memo } from "react";
import { useFormContext } from "react-hook-form";
import { AccountTimeZone } from "../../../../../constants/AccountTimeZone";
import { buildExplorerUrl } from "../../../../../helper/dataSource/buildExplorerUrl";
import { formatDateLocal } from "../../../../../helpers/formatDateLocal";
import { useClipboardToast } from "../../../../../hooks/useClipboardToast";
import { EditTransactionForm } from "../../../../../types/order/addOrderForm";
import { startOfMinuteInTimeZone } from "../../../../atoms/datePicker/tzDateUtils";
import { ImageWithMissingSrc } from "../../../../atoms/ImageWithMissingSrc";
import { DatePickerField } from "../../../../forms/DatePickerField";
import { InputField } from "../../../../forms/InputField";
import { SingleSelectorField } from "../../../../forms/SingleSelectorField";
import { TextareaField } from "../../../../forms/TextAreaField";
import { StandardSelectOption } from "../../../../selector/parts/SelectOption";
import {
  SectionContainer,
  SectionTitle,
  TransactionOverviewPaddingX,
} from "./SectionTitle";

type TransactionDetailsSectionProps = {
  transaction: TransactionEvent;
  editMode: boolean;
  updating: boolean;
  walletOptions: StandardSelectOption[] | undefined;
  transactionHash: string | undefined;
  transactionHashDataSourceCode: DataSourceCode | undefined;
};

export const TransactionDetailsSection = memo(
  function TransactionDetailsSection({
    transaction,
    editMode,
    updating,
    walletOptions,
    transactionHash,
    transactionHashDataSourceCode,
  }: TransactionDetailsSectionProps): React.ReactElement {
    return (
      <Box w="full">
        <SectionContainer>
          <SectionTitle>Details</SectionTitle>
        </SectionContainer>

        {editMode ?
          <TransactionDetailsEditFields
            updating={updating}
            walletOptions={walletOptions}
          />
        : <TransactionDetailsReadOnly
            transaction={transaction}
            transactionHash={transactionHash}
            transactionHashDataSourceCode={transactionHashDataSourceCode}
          />
        }
      </Box>
    );
  },
);

function TransactionDetailsGrid({
  children,
}: {
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <Grid
      w="full"
      minW={0}
      px={TransactionOverviewPaddingX}
      templateColumns={{
        base: "minmax(0, 1fr)",
        md: "repeat(2, minmax(0, 1fr))",
      }}
      columnGap={4}
      rowGap={4}
    >
      {children}
    </Grid>
  );
}

const EMPTY_OPTIONS: StandardSelectOption[] = [];

function TransactionDetailsEditFields({
  updating,
  walletOptions,
}: {
  updating: boolean;
  walletOptions: StandardSelectOption[] | undefined;
}): React.ReactElement {
  const form = useFormContext<EditTransactionForm>();

  return (
    <TransactionDetailsGrid>
      <GridItem>
        <SingleSelectorField
          form={form}
          name="walletId"
          label="Data source"
          isRequired
          isDisabled={updating}
          moveSelectedToTop
          enableSearch
          options={walletOptions ?? EMPTY_OPTIONS}
          buttonProps={{
            width: "full",
          }}
          popoverProps={{
            matchWidth: true,
          }}
        />
      </GridItem>

      <GridItem>
        <DatePickerField
          form={form}
          name="date"
          label="Date"
          isRequired
          isDisabled={updating}
          earliestDate={SYLA_EARLIEST_EVENT_DATE}
          latestDate={startOfMinuteInTimeZone(new Date(), AccountTimeZone)}
          showTime
          timeZone={AccountTimeZone}
        />
      </GridItem>

      <GridItem colSpan={{ base: 1, md: 2 }}>
        <TextareaField
          form={form}
          name="description"
          label="Description"
          isDisabled={updating}
        />
      </GridItem>

      <GridItem colSpan={{ base: 1, md: 2 }}>
        <InputField
          form={form}
          name="transactionHash"
          label="Transaction hash"
          isDisabled={updating}
        />
      </GridItem>
    </TransactionDetailsGrid>
  );
}

function TransactionDetailsReadOnly({
  transaction,
  transactionHash,
  transactionHashDataSourceCode,
}: {
  transaction: TransactionEvent;
  transactionHash: string | undefined;
  transactionHashDataSourceCode: DataSourceCode | undefined;
}): React.ReactElement {
  const { onCopy, hasCopied } = useClipboardToast({
    value: transactionHash,
    toastOptions: {
      title: "Copied transaction hash",
    },
  });

  const hasDescription = transaction.description !== "";

  return (
    <TransactionDetailsGrid>
      <GridItem>
        <ReadOnlyField label="Data source">
          <Flex align="center" gap={1}>
            <ImageWithMissingSrc
              src={transaction.dataSource.image}
              alt=""
              h="2rem"
              w="2rem"
            />
            <Text color="gray.600">{getWalletName(transaction)}</Text>
          </Flex>
        </ReadOnlyField>
      </GridItem>

      <GridItem>
        <ReadOnlyField label="Date">
          <Text color="gray.600">
            <Tooltip label={transaction.date.toISOString()}>
              {formatDateLocal(transaction.date, {
                timeZone: AccountTimeZone,
                dateStyle: "medium",
                timeStyle: "medium",
              })}
            </Tooltip>
          </Text>
        </ReadOnlyField>
      </GridItem>

      <GridItem colSpan={{ base: 1, md: 2 }}>
        <ReadOnlyField label="Description">
          <Text
            whiteSpace="pre-wrap"
            color={hasDescription ? "gray.600" : "gray.400"}
          >
            {hasDescription ? transaction.description : "None"}
          </Text>
        </ReadOnlyField>
      </GridItem>

      <GridItem colSpan={{ base: 1, md: 2 }}>
        <ReadOnlyField label="Transaction hash">
          {transactionHash ?
            <Flex direction="row" align="center" gap={2} minW={0}>
              <ExplorerLink
                transactionHash={transactionHash}
                dataSourceCode={transactionHashDataSourceCode}
              />

              <Button
                size="sm"
                variant="outline"
                colorScheme="gray"
                onClick={onCopy}
                flexShrink={0}
              >
                {hasCopied ? "Copied" : "Copy"}
              </Button>
            </Flex>
          : <Text color="gray.400">None</Text>}
        </ReadOnlyField>
      </GridItem>
    </TransactionDetailsGrid>
  );
}

function ReadOnlyField({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}): React.ReactElement {
  //  keep the styling of a FormLabel, but don't render it as a label
  return (
    <Flex direction="column">
      <FormLabel as="div">{label}</FormLabel>
      {children}
    </Flex>
  );
}

function ExplorerLink({
  transactionHash,
  dataSourceCode,
}: {
  transactionHash: string;
  dataSourceCode: DataSourceCode | undefined;
}): React.ReactElement {
  const explorerUrl = buildExplorerUrl({
    code: dataSourceCode,
    txHash: transactionHash,
  });

  if (!explorerUrl) {
    return (
      <Text color="gray.600" minW={0} overflowWrap="anywhere">
        {transactionHash}
      </Text>
    );
  }

  return (
    <Link
      minW={0}
      color="primary.700"
      isExternal
      href={explorerUrl}
      overflowWrap="anywhere"
      wordBreak="break-word"
    >
      {transactionHash}{" "}
      <ExternalLinkIcon
        display="inline-block"
        flexShrink={0}
        verticalAlign="text-bottom"
      />
    </Link>
  );
}
